import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/free-solid-svg-icons';
import { motion, AnimatePresence } from 'framer-motion';
import { City } from '../../../services/searchService';
import TimeSelect from '../../TimeSelect/TimeSelect';
import PlaceAutocompleteInput, { PlaceSelection } from '../../PlaceAutocompleteInput/PlaceAutocompleteInput';
import CitySelect from '../../CitySelect/CitySelect';
import {
  VerticalType,
  TripType,
  VERTICAL_OPTIONS,
  TRIP_TYPES_BY_VERTICAL,
  DEFAULT_TRIP_TYPE_BY_VERTICAL,
  isSourceFieldVisible,
  isDestinationFieldVisible,
  isSourceCityOnly,
  isDestinationCityOnly,
  isReturnLegRequired,
} from '../../../constants/tripTypes';

interface SearchSectionNewProps {
  activeTab: VerticalType;
  setActiveTab: (tab: VerticalType) => void;
  tripType: TripType;
  setTripType: (triptype: TripType) => void;
  source: City | null;
  setSource: (source: City | null) => void;
  destination: City | null;
  setDestination: (destination: City | null) => void;
  departureDate: string;
  setDepartureDate: (date: string) => void;
  returnDate: string;
  setReturnDate: (date: string) => void;
  pickupTime: string;
  setPickupTime: (time: string) => void;
  dropTime: string;
  setDropTime: (time: string) => void;
  sourceSearch: string;
  setSourceSearch: (search: string) => void;
  destSearch: string;
  setDestSearch: (search: string) => void;
  // Exact pickup/drop address (Google Places) - source/destination City is
  // derived from these (nearest match), since Price/Plan routes are
  // configured per city pair, not exact address (see Landing.tsx).
  onSourcePlaceSelected: (place: PlaceSelection) => void;
  onDestinationPlaceSelected: (place: PlaceSelection) => void;
  allCities: City[];
  onSourceCitySelected: (city: City) => void;
  onDestinationCitySelected: (city: City) => void;
  onSwapLocations: () => void;
  handleSearch: () => void;
  handleClearFilter: () => void;
  isSearchEnabled: boolean;
  showClearButton: boolean;
  animationEnabled?: boolean;
}

interface SearchSectionNewState {}

class SearchSectionNew extends React.Component<SearchSectionNewProps, SearchSectionNewState> {
  departureDateRef: React.RefObject<HTMLInputElement>;
  returnDateRef: React.RefObject<HTMLInputElement>;

  constructor(props: SearchSectionNewProps) {
    super(props);

    this.departureDateRef = React.createRef();
    this.returnDateRef = React.createRef();

    this.state = {};
  }

  componentDidUpdate() {
    const {
      activeTab,
      tripType,
      source,
      setSource,
      setSourceSearch,
      destination,
      setDestination,
      setDestSearch,
    } = this.props;

    // Clear a stale field whenever the current vertical/trip-type no longer
    // needs it (currently only Hourly's destination - both fields stay
    // visible for Airport, since each side is just a plain City).
    if (!isSourceFieldVisible(activeTab, tripType) && source !== null) {
      setSource(null);
      setSourceSearch('');
    }
    if (!isDestinationFieldVisible(activeTab, tripType) && destination !== null) {
      setDestination(null);
      setDestSearch('');
    }
  }

  formatDateForDisplay = (isoDate: string) => {
    if (!isoDate) return "";
    const date = new Date(isoDate);
    return `${date.getDate().toString().padStart(2, "0")}/${(
      date.getMonth() + 1
    )
      .toString()
      .padStart(2, "0")}/${date.getFullYear()}`;
  };

  clearSourceSearch = (e: React.MouseEvent) => {
    e.stopPropagation();
    this.props.setSourceSearch("");
    this.props.setSource(null);
  };

  clearDestSearch = (e: React.MouseEvent) => {
    e.stopPropagation();
    this.props.setDestSearch("");
    this.props.setDestination(null);
  };

  showDepartureDatePicker = () => {
    if (this.departureDateRef.current) {
      this.departureDateRef.current.showPicker();
    }
  };

  showReturnDatePicker = () => {
    if (this.returnDateRef.current) {
      this.returnDateRef.current.showPicker();
    }
  };

  selectVertical = (vertical: VerticalType) => {
    this.props.setActiveTab(vertical);
    this.props.setTripType(DEFAULT_TRIP_TYPE_BY_VERTICAL[vertical]);
  }

  render() {
    const {
      activeTab,
      tripType,
      setTripType,
      source,
      destination,
      departureDate,
      setDepartureDate,
      returnDate,
      setReturnDate,
      pickupTime,
      setPickupTime,
      dropTime,
      setDropTime,
      sourceSearch,
      setSourceSearch,
      destSearch,
      setDestSearch,
      onSourcePlaceSelected,
      onDestinationPlaceSelected,
      allCities,
      onSourceCitySelected,
      onDestinationCitySelected,
      onSwapLocations,
      handleSearch,
      handleClearFilter,
      isSearchEnabled,
      showClearButton,
      animationEnabled = true,
    } = this.props;

    // Only Hourly hides its destination field - Airport keeps both fields
    // visible, but one side must be a plain City pick matching a Plan (see
    // isSourceCityOnly/isDestinationCityOnly), the other a free address.
    const isPickUpVisible = isSourceFieldVisible(activeTab, tripType);
    const isDestinationVisible = isDestinationFieldVisible(activeTab, tripType);
    const isPickUpCityOnly = isSourceCityOnly(activeTab, tripType);
    const isDestinationCityOnlyField = isDestinationCityOnly(activeTab, tripType);
    // Return date/time apply to Round-Trip and Multi-City (both bill a
    // return leg back to source) - One-Way doesn't need one.
    const showReturnDateTime = isReturnLegRequired(tripType);

    const formVariants = {
      hidden: { opacity: 0 },
      visible: { opacity: 1, transition: { duration: 0.2, delay: 0.15 } },
      exit: { opacity: 0, transition: { duration: 0.1 } },
    };

    return (
      <section className="max-w-6xl mx-auto px-4 py-8">
        {/* Trip Vertical tabs */}
        <div className="flex gap-4 items-start justify-center md:justify-start mb-6 rounded-t-lg">
          {VERTICAL_OPTIONS.map((vertical) => (
            <button
              key={vertical.value}
              className={`px-4 py-2 font-medium text-sm rounded-full transition-colors duration-200 flex items-center gap-2 border-4 border-black ${
                activeTab === vertical.value ? "bg-white text-black" : "bg-black text-white"
              }`}
              onClick={() => this.selectVertical(vertical.value)}
            >
              <span>{vertical.label}</span>
            </button>
          ))}
        </div>

        <div className="flex gap-4 items-start justify-center md:justify-start mb-6 rounded-t-lg">
          {TRIP_TYPES_BY_VERTICAL[activeTab].map((option) => (
            <button
              key={option.value}
              className={`px-4 py-2 font-medium text-sm rounded-full transition-colors duration-200 flex items-center gap-2 border-4 border-black ${
                tripType === option.value ? "bg-white text-black" : "bg-black text-white"
              }`}
              onClick={() => setTripType(option.value)}
            >
              <span>{option.label}</span>
            </button>
          ))}
        </div>

        {/* Search form */}
        <div className="relative pb-14">
          <motion.div layout transition={{ duration: 0.3, ease: "easeInOut" }}>
            <AnimatePresence mode="wait">
              <motion.div
                key={activeTab}
                variants={formVariants}
                initial="hidden"
                animate="visible"
                exit="exit"
              >
                {/* Pickup and Dropoff Section */}
                <div className="flex items-stretch justify-between gap-6 flex-col md:flex-row">
                  {/* Pick-up Card - Date/Time always apply */}
                  <div className="flex-1 w-full bg-black text-white rounded-2xl shadow-xl p-6">
                    <div className="flex items-center gap-3 mb-6">
                      <span className="w-3 h-3 rounded-full bg-blue-600" />
                      <h3 className="text-lg font-semibold">Pick - Up</h3>
                    </div>

                    <div className="flex flex-col gap-4 text-sm">
                      {/* Location - own row, since it needs the most room */}
                      {isPickUpVisible && (
                      <div className="relative">
                        <div className="text-gray-300 font-medium mb-2">Locations</div>
                        <div className="flex items-center border border-gray-600 rounded-md p-2 bg-gray-900 relative">
                          <svg className="w-4 h-4 text-gray-400 mr-2 flex-shrink-0" viewBox="0 0 24 24" fill="none">
                            <path
                              d="M12 13C13.6569 13 15 11.6569 15 10C15 8.34315 13.6569 7 12 7C10.3431 7 9 8.34315 9 10C9 11.6569 10.3431 13 12 13Z"
                              stroke="currentColor"
                              strokeWidth="2"
                            />
                            <path
                              d="M12 22C16 18 20 14.4183 20 10C20 5.58172 16.4183 2 12 2C7.58172 2 4 5.58172 4 10C4 14.4183 8 18 12 22Z"
                              stroke="currentColor"
                              strokeWidth="2"
                            />
                          </svg>
                          {isPickUpCityOnly ? (
                            <CitySelect
                              className="flex-grow min-w-0 outline-none text-gray-200 placeholder-gray-500 bg-transparent text-xs"
                              placeholder="Select your city"
                              cities={allCities}
                              value={source}
                              onSelect={onSourceCitySelected}
                            />
                          ) : (
                            <PlaceAutocompleteInput
                              className="flex-grow min-w-0 outline-none text-gray-200 placeholder-gray-500 bg-transparent text-xs"
                              placeholder="Enter pickup address"
                              value={sourceSearch}
                              onChange={(text) => {
                                setSourceSearch(text);
                                if (source) this.props.setSource(null);
                              }}
                              onPlaceSelected={onSourcePlaceSelected}
                            />
                          )}
                          {(isPickUpCityOnly ? source : sourceSearch) && (
                            <FontAwesomeIcon
                              icon={faTimes}
                              className="text-gray-400 cursor-pointer hover:text-gray-200 text-xs flex-shrink-0 ml-2"
                              onClick={this.clearSourceSearch}
                            />
                          )}
                        </div>
                      </div>
                      )}

                      {/* Date + Time - shared row, Time needs more room than
                          a 3-way split can give it (icon + hour/minute/AM-PM
                          selects), so it only ever shares with Date. */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <div className="text-gray-300 font-medium mb-2">Date</div>
                          <div
                            className="flex items-center border border-gray-600 rounded-md p-2 bg-gray-900 cursor-pointer relative"
                            onClick={this.showDepartureDatePicker}
                          >
                            <svg className="w-4 h-4 text-gray-400 mr-2 flex-shrink-0" viewBox="0 0 24 24" fill="none">
                              <path
                                d="M8 7V3M16 7V3M7 11H17M5 21H19C20.1046 21 21 20.1046 21 19V7C21 5.89543 20.1046 5 19 5H5C3.89543 5 3 5.89543 3 7V19C3 20.1046 3.89543 21 5 21Z"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                            <input
                              type="text"
                              className="flex-grow outline-none text-gray-200 placeholder-gray-500 bg-transparent text-xs"
                              placeholder="dd/mm/yyyy"
                              value={departureDate ? this.formatDateForDisplay(departureDate) : ""}
                              readOnly
                            />
                            <input
                              ref={this.departureDateRef}
                              type="date"
                              className="absolute inset-0 opacity-0 cursor-pointer"
                              value={departureDate}
                              onChange={(e) => setDepartureDate(e.target.value)}
                              min={new Date().toISOString().split("T")[0]}
                            />
                          </div>
                        </div>

                        <div>
                          <div className="text-gray-300 font-medium mb-2">Time</div>
                          <div className="flex items-center border border-gray-600 rounded-md p-2 bg-gray-900">
                            <svg className="w-4 h-4 text-gray-400 mr-2 flex-shrink-0" viewBox="0 0 24 24" fill="none">
                              <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" />
                              <path d="M12 6V12L16 14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                            </svg>
                            <TimeSelect
                              value={pickupTime}
                              onChange={setPickupTime}
                              selectClassName="bg-gray-900 text-gray-200 text-xs outline-none cursor-pointer"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Swap Icon - only relevant when both fields are visible
                      and interchangeable (Airport's two fields aren't - one
                      must stay a City pick, the other a free address). */}
                  {(isPickUpVisible && isDestinationVisible && !isPickUpCityOnly && !isDestinationCityOnlyField) && (
                  <div className="flex flex-col items-center justify-center gap-4">
                    <button
                      onClick={onSwapLocations}
                      className="bg-blue-600 text-white rounded-full w-10 h-10 flex items-center justify-center hover:bg-blue-700 transition-colors"
                    >
                      <span className="text-lg">↕</span>
                    </button>
                  </div>
                  )}

                  {/* Drop-off Card */}
                  {isDestinationVisible && (
                  <div className="flex-1 w-full bg-black text-white rounded-2xl shadow-xl p-6">
                    <div className="flex items-center gap-3 mb-6">
                      <span className="w-3 h-3 rounded-full bg-blue-600" />
                      <h3 className="text-lg font-semibold">Drop - Off</h3>
                    </div>

                    <div className="flex flex-col gap-4 text-sm">
                      {/* Location - own row, since it needs the most room */}
                      <div className="relative">
                        <div className="text-gray-300 font-medium mb-2">Locations</div>
                        <div className="flex items-center border border-gray-600 rounded-md p-2 bg-gray-900 relative">
                          <svg className="w-4 h-4 text-gray-400 mr-2 flex-shrink-0" viewBox="0 0 24 24" fill="none">
                            <path
                              d="M12 13C13.6569 13 15 11.6569 15 10C15 8.34315 13.6569 7 12 7C10.3431 7 9 8.34315 9 10C9 11.6569 10.3431 13 12 13Z"
                              stroke="currentColor"
                              strokeWidth="2"
                            />
                            <path
                              d="M12 22C16 18 20 14.4183 20 10C20 5.58172 16.4183 2 12 2C7.58172 2 4 5.58172 4 10C4 14.4183 8 18 12 22Z"
                              stroke="currentColor"
                              strokeWidth="2"
                            />
                          </svg>
                          {isDestinationCityOnlyField ? (
                            <CitySelect
                              className="flex-grow min-w-0 outline-none text-gray-200 placeholder-gray-500 bg-transparent text-xs"
                              placeholder="Select your city"
                              cities={allCities}
                              value={destination}
                              onSelect={onDestinationCitySelected}
                            />
                          ) : (
                            <PlaceAutocompleteInput
                              className="flex-grow min-w-0 outline-none text-gray-200 placeholder-gray-500 bg-transparent text-xs"
                              placeholder="Enter drop address"
                              value={destSearch}
                              onChange={(text) => {
                                setDestSearch(text);
                                if (destination) this.props.setDestination(null);
                              }}
                              onPlaceSelected={onDestinationPlaceSelected}
                            />
                          )}
                          {(isDestinationCityOnlyField ? destination : destSearch) && (
                            <FontAwesomeIcon
                              icon={faTimes}
                              className="text-gray-400 cursor-pointer hover:text-gray-200 text-xs flex-shrink-0 ml-2"
                              onClick={this.clearDestSearch}
                            />
                          )}
                        </div>
                      </div>

                      {/* Date + Time - round-trip only, shared row (see
                          matching comment on the Pick-Up card). */}
                      {showReturnDateTime && (
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <div className="text-gray-300 font-medium mb-2">Date</div>
                          <div
                            className="flex items-center border border-gray-600 rounded-md p-2 bg-gray-900 cursor-pointer relative"
                            onClick={this.showReturnDatePicker}
                          >
                            <svg className="w-4 h-4 text-gray-400 mr-2 flex-shrink-0" viewBox="0 0 24 24" fill="none">
                              <path
                                d="M8 7V3M16 7V3M7 11H17M5 21H19C20.1046 21 21 20.1046 21 19V7C21 5.89543 20.1046 5 19 5H5C3.89543 5 3 5.89543 3 7V19C3 20.1046 3.89543 21 5 21Z"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                            <input
                              type="text"
                              className="flex-grow outline-none text-gray-200 placeholder-gray-500 bg-transparent text-xs"
                              placeholder="dd/mm/yyyy"
                              value={returnDate ? this.formatDateForDisplay(returnDate) : ""}
                              readOnly
                            />
                            <input
                              ref={this.returnDateRef}
                              type="date"
                              className="absolute inset-0 opacity-0 cursor-pointer"
                              value={returnDate}
                              onChange={(e) => setReturnDate(e.target.value)}
                              min={
                                departureDate ||
                                new Date().toISOString().split("T")[0]
                              }
                            />
                          </div>
                        </div>

                        <div>
                          <div className="text-gray-300 font-medium mb-2">Time</div>
                          <div className="flex items-center border border-gray-600 rounded-md p-2 bg-gray-900">
                            <svg className="w-4 h-4 text-gray-400 mr-2 flex-shrink-0" viewBox="0 0 24 24" fill="none">
                              <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" />
                              <path d="M12 6V12L16 14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                            </svg>
                            <TimeSelect
                              value={dropTime}
                              onChange={setDropTime}
                              selectClassName="bg-gray-900 text-gray-200 text-xs outline-none cursor-pointer"
                            />
                          </div>
                        </div>
                      </div>
                      )}
                    </div>
                  </div>
                  )}
                </div>
              </motion.div>
            </AnimatePresence>
          </motion.div>

          {/* Search and Clear buttons */}
          <div className="absolute left-1/2 bottom-0 transform -translate-x-1/2 translate-y-1/2 flex items-center gap-4">
            <button
              className={`text-white font-medium py-3 px-12 rounded-md shadow-md transition-colors duration-200 whitespace-nowrap ${
                !isSearchEnabled
                  ? "bg-blue-300 cursor-not-allowed"
                  : "bg-blue-600 hover:bg-blue-700"
              }`}
              onClick={handleSearch}
              disabled={!isSearchEnabled}
            >
              Search Cabs
            </button>
            {showClearButton && (
              <button
                className="bg-gray-200 hover:bg-gray-300 text-gray-800 font-medium py-3 px-12 rounded-md shadow-md transition-colors duration-200 whitespace-nowrap"
                onClick={handleClearFilter}
              >
                Clear Filter
              </button>
            )}
          </div>
        </div>
      </section>
    );
  }
}

export default SearchSectionNew;
